import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import WhatsAppFloat from "@/pages/home/components/WhatsAppFloat";
import AnnouncementBar from "@/pages/home/components/AnnouncementBar";
import { buildTimeline } from "@/pages/order/page";
import type { OrderWithTimeline } from "@/pages/order/page";
import { STATUS_CONFIG } from "@/pages/order/page";
import BankTransferStatus from "@/pages/order/components/BankTransferStatus";
import OrderTimeline from "@/pages/order/components/OrderTimeline";
import OrderItems from "@/pages/order/components/OrderItems";
import type { SavedOrder } from "@/hooks/useOrders";
import { useCallback } from "react";

const TRACK_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "تتبع الطلب — متجر الإبداع",
  "description": "تتبع حالة طلبك في متجر الإبداع AL Ebdaa. أدخل رقم الطلب ورقم جوالك لمعرفة موقع طلبك.",
  "url": `${import.meta.env.VITE_SITE_URL ?? "https://example.com"}/track-order`,
  "inLanguage": "ar-SA"
};

type SearchMethod = "phone" | "email" | "ref_only";

export default function TrackOrderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [barVisible, setBarVisible] = useState(true);
  const handleBarVisibility = useCallback((v: boolean) => setBarVisible(v), []);

  const prefillRef = searchParams.get("ref") ?? "";
  const [orderRef, setOrderRef] = useState(prefillRef.toUpperCase());
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [searchMethod, setSearchMethod] = useState<SearchMethod>(prefillRef ? "ref_only" : "phone");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState<OrderWithTimeline | null>(null);
  const orderRefRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-track-order";
    script.textContent = JSON.stringify(TRACK_SCHEMA);
    document.head.appendChild(script);
    document.title = "تتبع الطلب — متجر الإبداع AL Ebdaa";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "تتبع حالة طلبك في متجر الإبداع AL Ebdaa. أدخل رقم الطلب ورقم جوالك لمعرفة موقع طلبك.");
    return () => {
      const el = document.getElementById("schema-track-order");
      if (el) el.remove();
    };
  }, []);

  // ... existing code ...

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const ref = orderRef.trim();
    const ph = phone.trim().replace(/\s/g, "");
    const em = email.trim().toLowerCase();

    if (!ref) { setError("يرجى إدخال رقم الطلب"); return; }
    if (searchMethod === "phone" && !ph) { setError("يرجى إدخال رقم الجوال"); return; }
    if (searchMethod === "email" && !em) { setError("يرجى إدخال البريد الإلكتروني"); return; }
    if (searchMethod === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setError("يرجى إدخال بريد إلكتروني صحيح");
      return;
    }
    // ref_only: no extra validation needed

    setError("");
    setLoading(true);
    setOrder(null);

    try {
      const { data, error: fetchError } = await supabase
        .from("orders")
        .select("*")
        .eq("order_ref", ref)
        .maybeSingle();

      if (fetchError || !data) {
        setError("لم يتم العثور على طلب بهذا الرقم. تحقق من رقم الطلب وحاول مجدداً.");
        setLoading(false);
        return;
      }

      const normalize = (p: string) =>
        p.replace(/\s/g, "").replace(/^00966/, "0").replace(/^\+966/, "0");

      if (searchMethod === "phone") {
        // Verify phone matches
        if (normalize(String(data.phone)) !== normalize(ph)) {
          setError("رقم الجوال لا يتطابق مع هذا الطلب. تحقق من البيانات وحاول مجدداً.");
          setLoading(false);
          return;
        }
      } else if (searchMethod === "email") {
        // Verify email — look up customer by email then match customer_id
        if (!data.customer_id) {
          setError("هذا الطلب غير مرتبط ببريد إلكتروني. استخدم رقم الجوال للتحقق.");
          setLoading(false);
          return;
        }
        const { data: customer } = await supabase
          .from("customers")
          .select("id, email")
          .eq("id", data.customer_id)
          .maybeSingle();

        if (!customer || !customer.email || customer.email.toLowerCase() !== em) {
          setError("البريد الإلكتروني لا يتطابق مع هذا الطلب. تحقق من البيانات وحاول مجدداً.");
          setLoading(false);
          return;
        }
      }
      // ref_only: no verification — show order directly

      const orderData: SavedOrder = {
        orderRef: String(data.order_ref),
        date: String(data.created_at),
        name: String(data.name),
        phone: String(data.phone),
        city: String(data.city),
        address: String(data.address),
        notes: String(data.notes ?? ""),
        paymentLabel: String(data.payment_label),
        items: Array.isArray(data.items) ? data.items : [],
        totalPrice: Number(data.total_price),
        discountCode: String(data.discount_code ?? ""),
        discountPercent: Number(data.discount_percent ?? 0),
        discountAmount: Number(data.discount_amount ?? 0),
        finalPrice: Number(data.final_price),
        status: data.status as SavedOrder["status"],
        customerId: data.customer_id ? String(data.customer_id) : undefined,
      };

      setOrder({ ...orderData, timeline: buildTimeline(orderData) });
    } catch {
      setError("حدث خطأ أثناء البحث. يرجى المحاولة مرة أخرى.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setOrder(null);
    setError("");
    setOrderRef("");
    setPhone("");
    setEmail("");
    setTimeout(() => orderRefRef.current?.focus(), 100);
  };

  const cfg = order ? STATUS_CONFIG[order.status] : null;

  return (
    <div dir="rtl" className="bg-[#0a0a0a] min-h-screen" style={{ fontFamily: "'Cairo', sans-serif" }}>
      <AnnouncementBar onVisibilityChange={handleBarVisibility} />
      <Navbar barVisible={barVisible} />

      <main className="pt-28 pb-20 px-4">
        <div className="max-w-2xl mx-auto">

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs mb-8">
            <Link to="/" className="text-gray-600 hover:text-gray-400 transition-colors">المتجر</Link>
            <i className="ri-arrow-left-s-line text-gray-700"></i>
            <span className="text-gray-400">تتبع الطلب</span>
          </div>

          {/* Page Header */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-400/10 border border-amber-400/20 rounded-2xl mx-auto mb-4">
              <i className="ri-map-pin-time-line text-amber-400 text-3xl"></i>
            </div>
            <h1 className="text-white font-black text-2xl md:text-3xl mb-2">
              تتبع طلبك
            </h1>
            <p className="text-gray-500 text-sm">
              أدخل رقم الطلب ورقم جوالك لمعرفة حالة طلبك
            </p>
          </div>

          {/* Search Form */}
          {!order && (
            <form
              onSubmit={handleSearch}
              className="bg-[#141414] border border-white/5 rounded-2xl p-6 mb-6"
            >
              <div className="flex flex-col gap-5">
                {/* Order Ref */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-gray-400 text-xs font-medium">
                    رقم الطلب <span className="text-amber-400">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                      <i className="ri-hashtag text-gray-500 text-sm"></i>
                    </div>
                    <input
                      ref={orderRefRef}
                      type="text"
                      value={orderRef}
                      onChange={(e) => { setOrderRef(e.target.value.toUpperCase()); setError(""); }}
                      placeholder="مثال: EB123456"
                      className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl pr-10 pl-4 py-3.5 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600 font-mono tracking-wider"
                      dir="ltr"
                      style={{ textAlign: "right" }}
                    />
                  </div>
                  <p className="text-gray-600 text-[11px]">
                    رقم الطلب موجود في رسالة تأكيد الطلب عبر واتساب
                  </p>
                </div>

                {/* Method toggle */}
                <div className="flex flex-col gap-2">
                  <label className="text-gray-400 text-xs font-medium">
                    طريقة التحقق
                  </label>
                  <div className="flex gap-1.5 p-1 bg-[#0f0f0f] border border-white/5 rounded-xl">
                    <button
                      type="button"
                      onClick={() => { setSearchMethod("phone"); setError(""); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                        searchMethod === "phone"
                          ? "bg-amber-400 text-black"
                          : "text-gray-500 hover:text-gray-300"
                      }`}
                    >
                      <i className="ri-smartphone-line text-sm"></i>
                      <span className="hidden sm:inline">رقم الجوال</span>
                      <span className="sm:hidden">جوال</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setSearchMethod("email"); setError(""); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                        searchMethod === "email"
                          ? "bg-amber-400 text-black"
                          : "text-gray-500 hover:text-gray-300"
                      }`}
                    >
                      <i className="ri-mail-line text-sm"></i>
                      <span className="hidden sm:inline">البريد الإلكتروني</span>
                      <span className="sm:hidden">بريد</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setSearchMethod("ref_only"); setError(""); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                        searchMethod === "ref_only"
                          ? "bg-amber-400 text-black"
                          : "text-gray-500 hover:text-gray-300"
                      }`}
                    >
                      <i className="ri-hashtag text-sm"></i>
                      <span className="hidden sm:inline">رقم الطلب فقط</span>
                      <span className="sm:hidden">فقط</span>
                    </button>
                  </div>
                </div>

                {/* Phone input */}
                {searchMethod === "phone" && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-gray-400 text-xs font-medium">
                      رقم الجوال <span className="text-amber-400">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                        🇸🇦 +966
                      </span>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => { setPhone(e.target.value); setError(""); }}
                        placeholder="05XXXXXXXX"
                        className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl pr-28 pl-4 py-3.5 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600"
                        autoFocus
                      />
                    </div>
                    <p className="text-gray-600 text-[11px]">
                      نفس الرقم الذي استخدمته عند الطلب
                    </p>
                  </div>
                )}

                {/* Email input */}
                {searchMethod === "email" && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-gray-400 text-xs font-medium">
                      البريد الإلكتروني <span className="text-amber-400">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                        <i className="ri-mail-line text-gray-500 text-sm"></i>
                      </div>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(""); }}
                        placeholder="example@email.com"
                        className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl pr-10 pl-4 py-3.5 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600"
                        dir="ltr"
                        autoFocus
                      />
                    </div>
                    <p className="text-gray-600 text-[11px]">
                      البريد المستخدم عند إنشاء حسابك في المتجر
                    </p>
                  </div>
                )}

                {/* Ref only — info box */}
                {searchMethod === "ref_only" && (
                  <div className="flex items-start gap-3 bg-amber-400/8 border border-amber-400/20 rounded-xl px-4 py-3.5">
                    <i className="ri-information-line text-amber-400 text-base mt-0.5 flex-shrink-0"></i>
                    <div>
                      <p className="text-amber-300 text-xs font-bold mb-1">البحث برقم الطلب فقط</p>
                      <p className="text-gray-500 text-[11px] leading-relaxed">
                        سيتم عرض تفاصيل الطلب مباشرة بدون تحقق إضافي.
                        تأكد من أن رقم الطلب صحيح.
                      </p>
                    </div>
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="flex items-center gap-3 bg-red-400/8 border border-red-400/20 rounded-xl px-4 py-3">
                    <i className="ri-error-warning-line text-red-400 text-sm flex-shrink-0"></i>
                    <p className="text-red-400 text-xs">{error}</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 disabled:cursor-not-allowed text-black font-black py-4 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      جاري البحث...
                    </>
                  ) : (
                    <>
                      <i className="ri-search-line text-base"></i>
                      تتبع الطلب
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Result */}
          {order && cfg && (
            <div className="space-y-5">
              {/* Order Found Header */}
              <div className="bg-[#141414] border border-white/5 rounded-2xl overflow-hidden">
                <div className={`h-1 w-full ${
                  order.status === "delivered"
                    ? "bg-gradient-to-r from-teal-400/0 via-teal-400/80 to-teal-400/0"
                    : order.status === "cancelled"
                    ? "bg-gradient-to-r from-red-400/0 via-red-400/80 to-red-400/0"
                    : "bg-gradient-to-r from-amber-400/0 via-amber-400/80 to-amber-400/0"
                }`} />
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <p className="text-gray-500 text-xs mb-1">رقم الطلب</p>
                      <h2 className="text-white font-black text-2xl tracking-wide">
                        #{order.orderRef}
                      </h2>
                      <p className="text-gray-600 text-xs mt-1">
                        <i className="ri-calendar-line ml-1"></i>
                        {new Date(order.date).toLocaleDateString("ar-SA", {
                          year: "numeric", month: "long", day: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        })}
                      </p>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${cfg.bg} ${cfg.border}`}>
                      <i className={`${cfg.icon} ${cfg.color} text-base`}></i>
                      <span className={`${cfg.color} text-sm font-bold whitespace-nowrap`}>
                        {cfg.label}
                      </span>
                    </div>
                  </div>

                  {/* Quick info row */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-[#1a1a1a] rounded-xl p-3 text-center">
                      <p className="text-gray-600 text-[10px] mb-1">العميل</p>
                      <p className="text-white text-xs font-bold truncate">{order.name}</p>
                    </div>
                    <div className="bg-[#1a1a1a] rounded-xl p-3 text-center">
                      <p className="text-gray-600 text-[10px] mb-1">المدينة</p>
                      <p className="text-white text-xs font-bold">{order.city}</p>
                    </div>
                    <div className="bg-[#1a1a1a] rounded-xl p-3 text-center">
                      <p className="text-gray-600 text-[10px] mb-1">الإجمالي</p>
                      <p className="text-amber-400 text-xs font-black">{order.finalPrice.toLocaleString()} ر.س</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bank Transfer Status */}
              <BankTransferStatus order={order} />

              {/* Timeline */}
              <OrderTimeline order={order} />

              {/* Items */}
              <OrderItems order={order} />

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={`https://wa.me/966541138847?text=${encodeURIComponent(`استفسار عن طلب رقم: ${order.orderRef}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 text-[#25D366] font-bold py-3.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-base"></i>
                  استفسار عبر واتساب
                </a>
                <button
                  onClick={handleReset}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#141414] hover:bg-[#1a1a1a] border border-white/8 text-gray-300 font-bold py-3.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-search-line text-base"></i>
                  تتبع طلب آخر
                </button>
              </div>

              {/* Login CTA */}
              <div className="flex items-center gap-4 bg-amber-400/5 border border-amber-400/15 rounded-2xl px-5 py-4">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-400/15 rounded-xl flex-shrink-0">
                  <i className="ri-user-star-line text-amber-400 text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-bold">سجّل دخولك لتجربة أفضل</p>
                  <p className="text-gray-500 text-xs mt-0.5">تابع جميع طلباتك، اكسب نقاط مكافآت، واستمتع بمزايا حصرية</p>
                </div>
                <button
                  onClick={() => navigate("/auth")}
                  className="flex-shrink-0 bg-amber-400 hover:bg-amber-300 text-black font-black text-xs px-4 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap"
                >
                  تسجيل الدخول
                </button>
              </div>
            </div>
          )}

          {/* Help section */}
          {!order && (
            <div className="bg-[#141414] border border-white/5 rounded-2xl p-5">
              <h3 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center bg-amber-400/10 rounded-lg">
                  <i className="ri-question-line text-amber-400 text-sm"></i>
                </div>
                كيف أجد رقم طلبي؟
              </h3>
              <div className="space-y-3">
                {[
                  { icon: "ri-whatsapp-line", color: "text-green-400", bg: "bg-green-400/10", text: "رسالة واتساب التأكيد التي وصلتك بعد الطلب" },
                  { icon: "ri-user-line", color: "text-amber-400", bg: "bg-amber-400/10", text: "صفحة حسابي إذا كنت مسجلاً في المتجر" },
                  { icon: "ri-customer-service-2-line", color: "text-sky-400", bg: "bg-sky-400/10", text: "تواصل مع خدمة العملاء عبر واتساب" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center ${item.bg} rounded-lg flex-shrink-0`}>
                      <i className={`${item.icon} ${item.color} text-sm`}></i>
                    </div>
                    <p className="text-gray-400 text-xs">{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                <p className="text-gray-600 text-xs">لا تجد طلبك؟</p>
                <a
                  href="https://wa.me/966541138847"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-green-400 hover:text-green-300 text-xs font-bold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line"></i>
                  تواصل مع الدعم
                </a>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
